"use client";

import { useForumType } from '@/components/hooks/useForumType';
import React, { useState, useEffect } from 'react';
import { registerComponent } from '../../lib/vulcan-lib/components';
import { useOnNavigate } from '../hooks/useOnNavigate';
import { SearchBox, connectMenu } from 'react-instantsearch-dom';
import classNames from 'classnames';
import CloseIcon from '@/lib/vendor/@material-ui/icons/src/Close';
import IconButton from '@/lib/vendor/@material-ui/core/src/IconButton';
import withErrorBoundary from '../common/withErrorBoundary';
import { getSearchIndexName, getSearchClient, isSearchEnabled } from '../../lib/search/searchUtil';
import qs from 'qs'
import { useSearchAnalytics } from '../search/useSearchAnalytics';
import { useCurrentUser } from './withUser';
import { useNavigate } from '../../lib/routeUtil';
import { InstantSearch } from '../../lib/utils/componentsWithChildren';
import { createPortal } from 'react-dom';
import SearchBarResults from "../search/SearchBarResults";
import ForumIcon from "./ForumIcon";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const VirtualMenu = connectMenu(() => null);

const styles = defineStyles("SearchBar", (theme: ThemeType) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
  },
  rootChild: {
    height: 'fit-content'
  },
  searchInputArea: {
    display: "block",
    position: "relative",
    minWidth: 48,
    height: 48,

    "& .ais-SearchBox": {
      display: 'inline-block',
      position: 'relative',
      maxWidth: 300,
      width: '100%',
      height: 46,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      fontSize: 14,
    },
    "& .ais-SearchBox-form": {
      height: '100%'
    },
    "& .ais-SearchBox-submit":{
      display: "none"
    },
    // This is a class generated by React InstantSearch, which we don't have direct control over so
    // are doing a somewhat hacky thing to style it.
    "& .ais-SearchBox-input": {
      display:"none",

      height: "100%",
      width: "100%",
      paddingRight: 0,
      paddingLeft: 48,
      verticalAlign: "bottom",
      borderStyle: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      fontSize: 'inherit',
      "-webkit-appearance": "none",
      cursor: "text",
      borderRadius: 5,
    },
    "&.open .ais-SearchBox-input": {
      display:"inline-block",
    },
    "&.open .SearchBar-searchIconButton": {
      position: 'fixed',
    },
  },
  searchInputAreaSmall: {},
  searchIcon: {
    "--icon-size": "24px",
  },
  searchIconButton: {
    color: theme.palette.header.text ,
  },
  searchIconButtonSmall: {},
  closeSearchIcon: {
    fontSize: 14,
  },
  searchBarClose: {
    display: "inline-block",
    position: "absolute",
    top: 15,
    right: 5,
    cursor: "pointer"
  },
}))

const SearchBar = ({onSetIsActive, searchResultsArea}: {
  onSetIsActive: (active: boolean) => void,
  searchResultsArea: any,
}) => {
  const { isAF } = useForumType();
  const classes = useStyles(styles);
  const currentUser = useCurrentUser()
  const [inputOpen,setInputOpen] = useState(false);
  const [searchOpen,setSearchOpen] = useState(false);
  const [currentQuery,setCurrentQuery] = useState("");
  const navigate = useNavigate();
  const captureSearch = useSearchAnalytics();

  const handleSubmit = () => {
    navigate({pathname: `/search`, search: `?${qs.stringify({query: currentQuery})}`});
    closeSearch()
  }
  
  useOnNavigate(() => {
    closeSearch();
  });


  const openSearchResults = () => setSearchOpen(true);
  const closeSearchResults = () => setSearchOpen(false);

  const closeSearch = () => {
    setSearchOpen(false);
    setInputOpen(false);
    if (onSetIsActive)
      onSetIsActive(false);
  }

  const handleSearchTap = () => {
    setInputOpen(true);
    setSearchOpen(!!currentQuery);
    if (onSetIsActive)
      onSetIsActive(true);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') closeSearch();
    if (event.keyCode === 13) handleSubmit()
  }

  const queryStateControl = (searchState: any): void => {
    if (searchState.query !== currentQuery) {
      setCurrentQuery(searchState.query);
      if (searchState.query) {
        openSearchResults();
      } else {
        closeSearchResults();
      }
    }
  }

  useEffect(() => {
    if (currentQuery) {
      captureSearch("searchBar", {query: currentQuery});
    }
  }, [currentQuery, captureSearch])
  if (!isSearchEnabled()) {
    return <div>Search is disabled (ElasticSearch not configured on server)</div>
  }

  return <div className={classes.root} onKeyDown={handleKeyDown}>
    <div className={classes.rootChild}>
      <InstantSearch
        indexName={getSearchIndexName("Posts")}
        searchClient={getSearchClient({emptyStringSearchResults: "empty"})}
        onSearchStateChange={queryStateControl}
      >
        <div className={classNames(
          classes.searchInputArea,
          {"open": inputOpen},
          {[classes.searchInputAreaSmall]: !currentUser}
        )}>
          {isAF && <VirtualMenu attribute="af" defaultRefinement="true" />}
          <div onClick={handleSearchTap}>
            <IconButton className={classNames(classes.searchIconButton, {[classes.searchIconButtonSmall]: !currentUser})}>
              <ForumIcon icon="Search" className={classes.searchIcon} />
            </IconButton>
            {/* Ignored because SearchBox is incorrectly annotated as not taking null for its reset prop, when
              * null is the only option that actually suppresses the extra X button.
             // @ts-ignore */}
            {inputOpen && <SearchBox reset={null} focusShortcuts={[]} autoFocus={true} />}
          </div>
          { inputOpen && <div className={classes.searchBarClose} onClick={closeSearch}>
            <CloseIcon className={classes.closeSearchIcon}/>
          </div>}
          <div>
            {searchOpen && createPortal(
              <SearchBarResults closeSearch={closeSearch} currentQuery={currentQuery} />,
              searchResultsArea.current
            )}
          </div>
        </div>
      </InstantSearch>
    </div>
  </div>
}

export default registerComponent("SearchBar", SearchBar, {
  hocs: [withErrorBoundary],
  areEqual: "auto",
});


